import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Mail, Loader2 } from 'lucide-react';

interface EmailOTPChallengeProps {
  onVerified: () => void;
  onCancel: () => void;
  onSwitchMethod?: () => void;
  hasOtherMethods?: boolean;
}

export default function EmailOTPChallenge({ onVerified, onCancel, onSwitchMethod, hasOtherMethods }: EmailOTPChallengeProps) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const { toast } = useToast();

  const sendCode = async () => {
    setSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Nicht eingeloggt');

      const response = await supabase.functions.invoke('send-admin-otp', {
        body: { action: 'send' },
      });

      if (response.error) throw new Error(response.error.message);
      if (response.data?.error) throw new Error(response.data.error);

      setMaskedEmail(response.data.email || '');
      setCodeSent(true);
      toast({
        title: 'Code gesendet',
        description: 'Prüfe dein E-Mail-Postfach.',
      });
    } catch (error) {
      toast({
        title: 'Fehler',
        description: error instanceof Error ? error.message : 'Code konnte nicht gesendet werden.',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await supabase.functions.invoke('send-admin-otp', {
        body: { action: 'verify', code },
      });

      if (response.error) throw new Error(response.error.message);
      if (response.data?.error) throw new Error(response.data.error);

      if (response.data?.success) {
        toast({
          title: 'Erfolgreich verifiziert',
          description: 'Willkommen im Admin-Bereich',
        });
        onVerified();
      } else {
        throw new Error('Verifizierung fehlgeschlagen');
      }
    } catch (error) {
      toast({
        title: 'Code ungültig',
        description: error instanceof Error ? error.message : 'Bitte überprüfe den Code.',
        variant: 'destructive',
      });
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">E-Mail-Verifizierung</CardTitle>
          <CardDescription>
            {codeSent
              ? `Ein 6-stelliger Code wurde an ${maskedEmail} gesendet`
              : 'Wir senden dir einen Bestätigungscode per E-Mail'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!codeSent ? (
            <div className="space-y-4">
              <Button onClick={sendCode} className="w-full" disabled={sending}>
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sende Code...
                  </>
                ) : (
                  'Code per E-Mail senden'
                )}
              </Button>
            </div>
          ) : (
            <form onSubmit={verifyCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email-otp-code">Bestätigungscode</Label>
                <Input
                  id="email-otp-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading || code.length !== 6}>
                {loading ? 'Überprüfen...' : 'Bestätigen'}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  onClick={sendCode}
                  disabled={sending}
                >
                  {sending ? 'Sende...' : 'Code erneut senden'}
                </button>
              </div>
            </form>
          )}

          <div className="mt-4 flex flex-col items-center gap-2">
            {hasOtherMethods && onSwitchMethod && (
              <button
                type="button"
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
                onClick={onSwitchMethod}
              >
                Andere Methode verwenden
              </button>
            )}
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
              onClick={onCancel}
            >
              Abmelden
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
